/**
 * Pure helpers for working with `PinnedObjectRef` values in the renderer:
 *
 *  - `pinRefKey`     — stable string key for use in membership/equality tests.
 *  - `pinRefEquals`  — semantic equality (two refs that describe the same target).
 *  - `pinRefFromNode` — derive a `PinnedObjectRef` from a tree node so the row's
 *                       pin button knows what to pin. Returns `null` for nodes
 *                       that are not pinnable (e.g. columns, sub-folders).
 *
 * This module is intentionally state-free and side-effect-free — it must be safe
 * to call from anywhere in the renderer (row renderer, context menus, store
 * actions, tests).
 */

import type { DatabaseExplorerNode } from '../../../explorer/models/nodeTypes';
import type { PinnedObjectRef, PinnedWorkspaceRef } from '../../../../types/electron';
import { storageResourceKey, type StorageResourceRef } from '../../../../types/azureStorage';
import { isStorageResourceNodeType } from '../../../explorer/models/azureStorageNodeFactories';

/**
 * Renderer-side mirror of `isWorkspacePinRef` in
 * `electron/metaInfo/connectionMetaInfo.ts` (the canonical definition — keep
 * the kind list in sync with `PinnedWorkspaceRef` there).
 *
 * We deliberately re-declare it here instead of importing the electron value:
 * the electron module lives in a referenced *composite* TS project and pulls in
 * `node:fs`/`node:events`, so a runtime `import { isWorkspacePinRef }` from it
 * into renderer `src/` breaks `tsc --noEmit` (TS6305 — the referenced project's
 * declaration output isn't built) and the production `tsc` build. `types/electron.d.ts`
 * can only re-export the *types* (a `.d.ts` can't re-export a value), so this
 * pure predicate is the renderer's type-safe entry point. The return type is the
 * shared `PinnedWorkspaceRef`, so callers still narrow correctly.
 */
const WORKSPACE_PIN_KINDS: readonly PinnedWorkspaceRef['kind'][] = [
  'tableView', 'query', 'notebook', 'dataCompare', 'schemaCompare',
];
export function isWorkspacePinRef(ref: PinnedObjectRef): ref is PinnedWorkspaceRef {
  return (WORKSPACE_PIN_KINDS as readonly string[]).includes(ref.kind);
}

/**
 * The set of folder kinds we support pinning, derived from `PinnedObjectRef`
 * so this module stays in lock-step with the canonical type.
 */
type FolderKind = Extract<PinnedObjectRef, { kind: 'folder' }>['folder'];

/**
 * The set of leaf object kinds we support pinning, derived from `PinnedObjectRef`.
 */
type ObjectKind = Extract<PinnedObjectRef, { kind: 'object' }>['objectType'];

/**
 * Stable string key for a `PinnedObjectRef`. Equal refs always produce equal
 * keys; refs that differ in any field (including `kind`) produce different keys.
 *
 * The key is the JSON encoding of a fixed-arity tuple. A delimiter-joined string
 * (any single separator — the previous '/' and the later '<->') collides when an
 * identifier contains that separator: two DISTINCT refs whose fields differ only
 * by where the separator sits join to the same string. Quoted SQL identifiers
 * can legally contain both '/' and '<->', so neither is safe. `JSON.stringify`
 * quotes and escapes every element, so the encoding is unambiguous regardless of
 * identifier content. (Must stay byte-identical to the on-disk `pinRefKey` in
 * `electron/metaInfo/connectionMetaInfo.ts` so renderer dedup matches disk dedup.)
 */
export function pinRefKey(ref: PinnedObjectRef): string {
  switch (ref.kind) {
    case 'database':
      return JSON.stringify(['db', ref.database]);
    case 'schema':
      return JSON.stringify(['schema', ref.database, ref.schema]);
    case 'folder':
      return JSON.stringify(['folder', ref.database, ref.folder]);
    case 'object':
      return JSON.stringify(['obj', ref.database, ref.objectType, ref.schema, ref.name]);
    // Content-bearing workspace pins dedup by id alone (mirrors the on-disk
    // `pinRefKey` in electron/metaInfo/connectionMetaInfo.ts) — a pin's
    // payload/name can be edited in place, so identity is the stable id, not
    // the content.
    case 'tableView':
    case 'query':
    case 'notebook':
    case 'dataCompare':
    case 'schemaCompare':
      return JSON.stringify([ref.kind, ref.id]);
    // Azure Storage (spec §8.3): identity is the typed ref's key; the label is
    // presentation and never part of the identity.
    case 'storage':
      return JSON.stringify(['storage', storageResourceKey(ref.resource)]);
  }
  // Unreachable when `PinnedObjectRef` stays in sync with the switch above.
  throw new Error(`pinRefKey: unknown PinnedObjectRef kind: ${JSON.stringify(ref)}`);
}

/**
 * Semantic equality between two pin refs — equivalent to `pinRefKey(a) === pinRefKey(b)`.
 */
export function pinRefEquals(a: PinnedObjectRef, b: PinnedObjectRef): boolean {
  return pinRefKey(a) === pinRefKey(b);
}

/**
 * Map of folder-leaf node `type` discriminators → corresponding `FolderKind`.
 * Keys must match the exact literal values declared on the folder node types
 * in `src/features/explorer/models/nodeTypes.ts`.
 */
const FOLDER_TYPE_TO_KIND: Record<string, FolderKind> = {
  'tables-folder': 'tables',
  'views-folder': 'views',
  'stored-procedures-folder': 'storedProcedures',
  'functions-folder': 'functions',
  'programmability-folder': 'programmability',
  // Oracle-specific folders — emitted by `createDatabaseObjectTypeFolders`
  // when engine === 'oracle'. They live directly under the database node
  // (not under a schema container), so the same database-scoped pin shape
  // works for them.
  'packages-folder': 'packages',
  'sequences-folder': 'sequences',
  'synonyms-folder': 'synonyms',
  'db-links-folder': 'dbLinks',
  'materialized-views-folder': 'materializedViews',
  'types-folder': 'types',
  'directories-folder': 'directories',
  // SQL Server `Programmability -> Types` leaves. The `user-types-folder`
  // container is deliberately absent — it has no listing to pin.
  'table-types-folder': 'tableTypes',
  'alias-types-folder': 'aliasTypes',
  'clr-types-folder': 'clrTypes',
};

/**
 * Map of object-leaf node `type` discriminators → corresponding `ObjectKind`.
 * Keys must match the exact literal values from `DatabaseObjectType` in
 * `src/features/database-objects/models/databaseObjectTypes.ts`.
 */
const OBJECT_TYPE_TO_KIND: Record<string, ObjectKind> = {
  'table': 'table',
  'view': 'view',
  'stored-procedure': 'storedProcedure',
  'function': 'function',
  // Oracle-specific leaf objects. The keys are the kebab-case
  // `DatabaseObjectType` literals embedded in object tree-node ids; the
  // values are the camelCase on-disk `ObjectKind`s declared in
  // `electron/metaInfo/connectionMetaInfo.ts`.
  'package': 'package',
  'sequence': 'sequence',
  'synonym': 'synonym',
  'db-link': 'dbLink',
  'materialized-view': 'materializedView',
  'type': 'type',
  'directory': 'directory',
  // SQL Server user-defined types.
  'table-type': 'tableType',
  'alias-type': 'aliasType',
  'clr-type': 'clrType',
};

/**
 * Derive a `PinnedObjectRef` from a tree node, or `null` if the node is not a
 * pinnable target. Pinnable nodes:
 *   - `database`
 *   - any leaf folder (tables-folder / views-folder / stored-procedures-folder /
 *     functions-folder / programmability-folder)
 *   - any leaf object (table / view / stored-procedure / function)
 *
 * All other node types (connections, sub-folders, columns, keys, etc.) return null.
 */
export function pinRefFromNode(node: DatabaseExplorerNode): PinnedObjectRef | null {
  // Workspace pins (tableView / query / notebook) are created from workspace
  // toolbars/tab menus, never derived from a tree node — a `pinned-workspace`
  // node already carries its ref on `node.pinRef` (read directly by callers),
  // so there is nothing to reconstruct here.
  if (node.type === 'pinned-workspace') return null;

  // Azure Storage resources pin by typed ref (spec §8.3); a pinned wrapper
  // already carries its ref.
  if (node.type === 'pinned-storage') return node.pinRef;
  if (isStorageResourceNodeType(node.type)) {
    const md = node.metadata as { resource?: StorageResourceRef };
    if (!md.resource) return null;
    return { kind: 'storage', resource: md.resource, label: node.name };
  }

  if (node.type === 'database') {
    const md = node.metadata as { name?: string; database?: string };
    const database = md.name ?? md.database ?? node.name;
    return { kind: 'database', database };
  }

  if (node.type === 'schema-container-folder') {
    const md = node.metadata as { database?: string; schema?: string };
    if (!md.database || !md.schema) return null;
    return { kind: 'schema', database: md.database, schema: md.schema };
  }

  const folderKind = FOLDER_TYPE_TO_KIND[node.type];
  if (folderKind !== undefined) {
    const md = node.metadata as { database?: string; schema?: string };
    if (!md.database) return null;
    // Schema-scoped folders (Tables/Views/... living inside a schema container
    // under schema grouping) are NOT pinnable: `PinnedObjectRef` carries no
    // schema for folders, so every schema's same-named folder would dedupe to
    // one schema-less pin and expand as all-DB objects (review finding P1/P2).
    // Pin the enclosing schema container instead — that preserves the schema.
    if (md.schema) return null;
    return { kind: 'folder', database: md.database, folder: folderKind };
  }

  const objectKind = OBJECT_TYPE_TO_KIND[node.type];
  if (objectKind !== undefined) {
    // Leaf object nodes (table / view / stored-procedure / function) wrap
    // their identity in `metadata.objectDetails` — that's what
    // `createDatabaseObjectNode` actually builds. The inner DatabaseObject
    // carries `schema` and `name` directly, but `database` is nested one
    // more level deep at `objectDetails.metadata.database`. The PinnedObjectNode
    // wrappers from Task 4 store everything at the top of `metadata`, so accept
    // either shape.
    const md = node.metadata as {
      database?: string;
      schema?: string;
      name?: string;
      objectDetails?: {
        schema?: string;
        name?: string;
        metadata?: { database?: string };
      };
    };
    const database = md.database ?? md.objectDetails?.metadata?.database;
    // Schemaless engines (Kusto/ADX: cluster → database → table, no schema
    // layer) list objects with `schema: ''`. Coalesce a missing schema to ''
    // and DON'T treat an empty schema as "not pinnable" — the ref still carries
    // an unambiguous (database, objectType, name) identity, and `pinRefKey`
    // JSON-encodes the empty schema without collision.
    const schema = md.schema ?? md.objectDetails?.schema ?? '';
    const name = md.name ?? md.objectDetails?.name;
    if (!database || !name) return null;
    return { kind: 'object', database, objectType: objectKind, schema, name };
  }

  return null;
}
